import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { Button, Card, CardContent, Stack, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import GeneralLoading from "../../../shared-components/Loading";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useApiClient } from "../../../api";
import { QuestionAccordion } from "../../question/components/QuestionAccordion";
import { AssignmentFromTemplateDialog } from "../components/AssignmentFromTemplateDialog";
import { formatUsageCount } from "../../../utils/formatters";
import { withPageTracking } from "../../../services/postHog";

const AssignmentTemplateSummary = ({ assignmentTemplate, onUseClicked }) => {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="h3">Assignment template</Typography>
          <Button endIcon={<Add />} variant="outlined" onClick={onUseClicked}>
            Use this
          </Button>
        </Stack>
        <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
          <Grid size={3}>
            <InformationSnippet
              title={"Subjects"}
              value={assignmentTemplate.subjects.map((s) => s.name).join(", ")}
            />
          </Grid>
          <Grid size={3}>
            <InformationSnippet
              title={"Education Level"}
              value={assignmentTemplate.educationLevel.level}
            />
          </Grid>
          <Grid size={3}>
            <InformationSnippet
              title={"Popularity"}
              value={formatUsageCount(assignmentTemplate.popularity)}
            />
          </Grid>
          <Grid size={3}>
            <InformationSnippet
              title={"Tags"}
              useTagBox
              value={assignmentTemplate.tags}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const AssignmentTemplateView = () => {
  const { assignmentTemplateId } = useParams();
  const [searchParams] = useSearchParams();
  const reuse = searchParams.get("reuse") === "True";
  const ApiClient = useApiClient();

  const [assignmentTemplate, setAssignmentTemplate] = useState({});
  const [open, setOpen] = useState(reuse);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAssignmentTemplate();
  }, [assignmentTemplateId]);

  const fetchAssignmentTemplate = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.assignment.getAssignmentTemplate({
        assignmentTemplate: assignmentTemplateId,
      });
      if (response) {
        setAssignmentTemplate(response.assignmentTemplate);
      }
    } catch (error) {
      console.error("Failed to fetch assignment", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (!assignmentTemplate || !Object.keys(assignmentTemplate).length) {
    return <Typography>Unable to load assignment template</Typography>;
  }
  return (
    <FruitifyContainer>
      <AssignmentFromTemplateDialog
        assignmentTemplate={assignmentTemplate}
        open={open}
        setOpen={setOpen}
      />
      <AssignmentTemplateSummary
        assignmentTemplate={assignmentTemplate}
        onUseClicked={() => setOpen(true)}
      />
      <Grid container spacing={4} direction={"row"} sx={{ mt: 2, mb: 2 }}>
        {assignmentTemplate?.assignmentTemplateQuestions.map(
          (assignmentQuestion, index) => (
            <Grid size={12} key={index}>
              <QuestionAccordion
                question={assignmentQuestion.question}
                number={index + 1}
                disableEdit
              />
            </Grid>
          ),
        )}
      </Grid>
    </FruitifyContainer>
  );
};

export default withPageTracking(AssignmentTemplateView, {
  feature: "assignment-template-view",
});
